import {
  Focus,
  UserRound,
  BriefcaseBusiness,
  UserPlus,
  BrainCircuit,
  Boxes,
  View,
  Sparkles,
  FileDown,
  Info,
  Database,
  type LucideIcon,
} from "lucide-react";
import type { FieldDefinition } from "../../../core/assistant/utils/discoverNodeFields";
import {
  evaluateConditions,
  type NodeData,
} from "../../../core/assistant/utils/conditionEvaluation";
import type { ViewRule } from "../../../core/assistant/models/ViewRules";
import {
  DEFAULT_ORG_PERSON_SHAPE_TYPE,
  isOrgPersonNodeType,
} from "../models/defaults";
import { isOrgNodeType } from "../constants/orgNodeTypes";

export const ACTION_LABELS: Record<string, string> = {
  "org-add-person": "Add Person",
  "org-add-position": "Add Position",
  "org-reporting": "Add Report",
  "org-occupies": "Reassign",
  "org-add-subordinate": "Add Person",
  "org-add-manager": "Add Manager",
};

// Icon mapping for quick action buttons
export const ACTION_ICONS: Record<string, LucideIcon> = {
  "org-focus-team": Focus,
  "org-add-person": UserRound,
  "org-add-position": BriefcaseBusiness,
  "org-add-subordinate": UserPlus,
  "org-add-manager": BriefcaseBusiness,
  "org-reporting": UserPlus,
  "org-occupies": UserPlus,
  scenarios: BrainCircuit,
  projects: Boxes,
  viewpoints: View,
  "ai-generate": Sparkles,
  import: FileDown,
  learn: Info,
  "data-source": Database,
};

export const TOOL_ORDER = [
  "org-add-person",
  "org-add-position",
  "org-reporting",
  "org-occupies",
];

// Temporary product gate: keep AI generate implementation code in place,
// but hide access from the assistant UI until the flow is production-ready.
export const ENABLE_ORG_AI_GENERATE = false;
export const ENABLE_ASSISTANT_INLINE_INSIGHTS = false;
export const ENABLE_ORG_PROJECTS = false;
export const DRAW_ORG_CHART_VIEW_ID = "draw-manually";
export const WORKLOAD_CONTEXT_VIEW_ID = "workload-context";

export function buildOrgChartBaseFields(
  tr: (key: string, opts?: { defaultValue: string }) => string,
): FieldDefinition[] {
  return [
    {
      id: "name",
      label: tr("orgchart.assistant.fields.name", { defaultValue: "Name" }),
      type: "text",
    },
    {
      id: "title",
      label: tr("orgchart.assistant.fields.title", { defaultValue: "Job Title" }),
      type: "text",
    },
    {
      id: "department",
      label: tr("orgchart.assistant.fields.department", {
        defaultValue: "Department",
      }),
      type: "select",
      options: [],
    },
    {
      id: "reportsTo",
      label: tr("orgchart.assistant.fields.reportsTo", { defaultValue: "Reports To" }),
      type: "text",
    },
    {
      id: "location",
      label: tr("orgchart.assistant.fields.location", { defaultValue: "Location" }),
      type: "select",
      options: [],
    },
    {
      id: "startDate",
      label: tr("orgchart.assistant.fields.startDate", {
        defaultValue: "Start Date",
      }),
      type: "date",
    },
    {
      id: "directReports",
      label: tr("orgchart.assistant.fields.directReports", {
        defaultValue: "Direct Reports",
      }),
      type: "number",
    },
    {
      id: "level",
      label: tr("orgchart.assistant.fields.level", { defaultValue: "Org Level" }),
      type: "number",
    },
    {
      id: "vacant",
      label: tr("orgchart.assistant.fields.vacant", {
        defaultValue: "Vacancy Status",
      }),
      type: "select",
      options: [
        {
          value: "true",
          label: tr("orgchart.assistant.fields.vacantOptionTrue", {
            defaultValue: "Vacant",
          }),
        },
        {
          value: "false",
          label: tr("orgchart.assistant.fields.vacantOptionFalse", {
            defaultValue: "Filled",
          }),
        },
      ],
    },
  ];
}
export const isOrgNode = (node?: { type?: string }) => isOrgNodeType(node?.type);
export { ORG_PERSON_NODE_TYPES as ORG_PERSON_TYPES } from "../constants/orgNodeTypes";
export type OrgChartCsvFilterCache = {
  records: {
    get: (key: string) => unknown;
    forEach?: (cb: (value: unknown, key: string) => void) => void;
  };
  children: {
    get: (key: string) => unknown;
  };
  totals: {
    get: (key: string) => unknown;
  };
};
export const REPORTING_EDGE_TYPES = new Set([
  "reporting",
  "reports_to",
  "dotted",
  "matrix",
  "team",
]);

export const getPersonShapeType = (node?: {
  type?: string;
}): "photo" | "text" | "card" => {
  if (!node) return DEFAULT_ORG_PERSON_SHAPE_TYPE;
  if (node.type === "org_person_text") return "text";
  if (node.type === "org_person_photo_inner_left") return "card";
  return "photo";
};

export const getSelectionPersonShapeType = (
  people: Array<{ type?: string }>,
): "photo" | "text" | "card" => {
  if (people.length === 0) return DEFAULT_ORG_PERSON_SHAPE_TYPE;
  const firstType = getPersonShapeType(people[0]);
  const isUniform = people.every(
    (person) => getPersonShapeType(person) === firstType,
  );
  return isUniform ? firstType : DEFAULT_ORG_PERSON_SHAPE_TYPE;
};

/** Returns the actual node type (e.g. org_person_photo_inner_left) when selection is uniform; otherwise null. */
export const getSelectionPersonNodeType = (
  people: Array<{ type?: string }>,
): string | null => {
  if (people.length === 0) return null;
  const first = people[0];
  if (!first?.type || !isOrgPersonNodeType(first.type)) return null;
  const isUniform = people.every(
    (p) => p?.type && isOrgPersonNodeType(p.type) && p.type === first.type,
  );
  return isUniform ? first.type : null;
};

export const areStringSetsEqual = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
};

export const matchesEveryRule = (nodeData: NodeData, rules: ViewRule[]): boolean =>
  rules.every((rule) => evaluateConditions(nodeData, rule.conditions, rule.logic));

export const matchesAnyRule = (nodeData: NodeData, rules: ViewRule[]): boolean =>
  rules.some((rule) => evaluateConditions(nodeData, rule.conditions, rule.logic));

export const createNodeId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
export const createEdgeId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
